import {
    BUILDING_OVERVIEW_DATAPOINTS,
    buildingFloorAreaSources,
    buildingFloorAreaValue,
    buildingLotSources,
    buildingLotsValue,
    contextForDatapointCategory,
} from '@quipex/feature/building-context';
import type {
    BuildingInfoSource,
    BuildingOverviewDatapointKey,
} from '@quipex/feature/building-context';
import {
    BuildingSubPremiseType,
    getBuildingPrimaryPropertyTypeName,
    getBuildingSubPremiseTypeName,
    type BuildingDetailsResponse,
} from '@quipex/shared/contracts';
import type {
    FocusedBuildingMetadataItem,
    FocusedBuildingViewModel,
} from './models';

const UNKNOWN_VALUE = 'Unknown';

export function toFocusedBuildingViewModel(
    building: BuildingDetailsResponse
): FocusedBuildingViewModel {
    return {
        address: building.address,
        buildingIdentificationNumbers:
            Array.from(
                new Set(
                    [
                        building.geoscapeProfile?.geoscapeBuildingPid,
                        ...(building.selectedAddressContext
                            ?.relatedBuildingPids ?? []),
                    ]
                        .map((pid) => pid?.trim())
                        .filter((pid): pid is string => Boolean(pid))
                )
            ).join(', ') || 'Not yet available',
        id: building.id,
        metadata: createMetadata(building),
        name: building.name,
        primaryPropertyType: building.primaryPropertyType,
        propertyType: getBuildingPrimaryPropertyTypeName(
            building.primaryPropertyType
        ),
        subPremiseType: building.subPremiseType ?? null,
        subPremiseTypeName:
            building.isSecondaryAddress &&
            building.subPremiseType != null &&
            building.subPremiseType !== BuildingSubPremiseType.Unknown
                ? getBuildingSubPremiseTypeName(building.subPremiseType)
                : null,
        status: building.status,
    };
}

function createMetadata(
    building: BuildingDetailsResponse
): readonly FocusedBuildingMetadataItem[] {
    return [
        createMetadataItem('about', building.about?.trim() || UNKNOWN_VALUE),
        createMetadataItem(
            'floor_area',
            buildingFloorAreaValue(building),
            buildingFloorAreaSources(building)
        ),
        createMetadataItem(
            'lots',
            buildingLotsValue(building),
            buildingLotSources(building)
        ),
        createMetadataItem(
            'occupancy_issued',
            formatDate(building.occupancyCertificateDate)
        ),
        createMetadataItem(
            'effective_height',
            formatNumber(building.effectiveHeight, 'm')
        ),
    ];
}

function createMetadataItem(
    definitionKey: BuildingOverviewDatapointKey,
    value: string,
    sources?: readonly BuildingInfoSource[]
): FocusedBuildingMetadataItem {
    const definition = BUILDING_OVERVIEW_DATAPOINTS[definitionKey];
    return {
        definitionKey,
        context: contextForDatapointCategory(definition.category),
        icon: definition.icon,
        label: definition.label,
        sources,
        unknown: value === UNKNOWN_VALUE,
        value,
    };
}

function formatNumber(value: number | null | undefined, unit: string): string {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return UNKNOWN_VALUE;
    }

    const formatted = new Intl.NumberFormat('en-AU', {
        maximumFractionDigits: 1,
    }).format(value);

    return `${formatted} ${unit}`;
}

function formatDate(value: string | null | undefined): string {
    if (!value?.trim()) return UNKNOWN_VALUE;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return UNKNOWN_VALUE;

    return new Intl.DateTimeFormat('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(date);
}
